import React, { useCallback, useEffect, useState } from 'react';
import {
    AlertCircle, Eye, Loader2, Mail, MessageSquare, MousePointerClick, Send, X, Zap,
} from 'lucide-react';
import { getContactCommsLog, getContactCommsMessage } from '../../../api/contact';
import { formatMelbourneDateTime } from '../../../utils/melbourneTime';

/**
 * Every email and text this contact was sent - campaigns, Find Buyers blasts
 * and the automated ones (buyer alerts, digests, open-home thank-yous, enquiry
 * confirmations, offer and signing emails).
 *
 * Christian Gravias, Monday 13198163292: "Automated emails and text messages
 * are not currently being recorded against contact cards. This needs to be
 * added so the full communication history is visible to agents."
 */

const PAGE_SIZE = 50;

const FILTERS = [
    { id: '', label: 'All', countKey: 'total' },
    { id: 'email', label: 'Emails', countKey: 'email' },
    { id: 'sms', label: 'Texts', countKey: 'sms' },
];

function formatWhen(dateStr) {
    return dateStr ? formatMelbourneDateTime(dateStr) : '';
}

function StatusChips({ row }) {
    const chips = [];
    if (row.status === 'failed' || row.status === 'bounced') {
        chips.push({ key: 'failed', label: row.status === 'bounced' ? 'Bounced' : 'Failed', cls: 'bg-red-50 text-red-600' });
    }
    if (row.status === 'skipped') {
        chips.push({ key: 'skipped', label: 'Not sent', cls: 'bg-gray-100 text-gray-500' });
    }
    if (row.opened_at) chips.push({ key: 'opened', label: 'Opened', icon: Eye, cls: 'bg-emerald-50 text-emerald-700' });
    if (row.clicked_at) chips.push({ key: 'clicked', label: 'Clicked', icon: MousePointerClick, cls: 'bg-blue-50 text-blue-700' });
    if (row.unsubscribed_at) chips.push({ key: 'unsub', label: 'Unsubscribed', cls: 'bg-amber-50 text-amber-700' });
    if (!chips.length) return null;
    return (
        <div className="flex flex-wrap gap-1.5 mt-2">
            {chips.map(({ key, label, icon: Icon, cls }) => (
                <span key={key} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
                    {Icon && <Icon className="w-3 h-3" />}
                    {label}
                </span>
            ))}
        </div>
    );
}

function MessageModal({ contactId, row, onClose }) {
    const [message, setMessage] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        getContactCommsMessage(contactId, row.id)
            .then((res) => { if (!cancelled) setMessage(res.data); })
            .catch(() => { if (!cancelled) setError('Could not load this email.'); });
        return () => { cancelled = true; };
    }, [contactId, row.id]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                <div className="flex items-start justify-between gap-4 p-4 border-b border-gray-100">
                    <div className="min-w-0">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-400">{row.source_label}</div>
                        <div className="font-semibold text-gray-900 truncate">{message?.subject || row.subject}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                            {formatWhen(row.occurred_at)}{row.to ? ` · to ${row.to}` : ''}
                            {message?.rendered_for ? ` · copy shown as sent to ${message.rendered_for}` : ''}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg hover:bg-gray-100">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    {!error && !message && (
                        <div className="flex items-center justify-center py-12 text-gray-400">
                            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading email…
                        </div>
                    )}
                    {message && message.html && (
                        <iframe
                            title="Email as sent"
                            srcDoc={message.html}
                            sandbox=""
                            className="w-full h-[65vh] border border-gray-100 rounded-lg bg-white"
                        />
                    )}
                    {message && !message.html && message.text && (
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{message.text}</p>
                    )}
                    {message && !message.html && !message.text && (
                        <p className="text-sm text-gray-500">
                            No copy of this email was kept, only its subject line. Emails that carry a
                            personal link (signing, offers, invites) are recorded this way on purpose.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

function CommsRow({ row, onView }) {
    const isSms = row.channel === 'sms';
    const Icon = isSms ? MessageSquare : Mail;
    return (
        <div className="border border-gray-100 rounded-xl p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#55c5d0]/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-[#55c5d0]" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-semibold text-gray-900 text-sm">{row.source_label}</span>
                    {row.automated ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">
                            <Zap className="w-3 h-3" /> Automated
                        </span>
                    ) : row.sent_by_name ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                            <Send className="w-3 h-3" /> Sent by {row.sent_by_name}
                        </span>
                    ) : null}
                    <span className="text-xs text-gray-400 ml-auto">{formatWhen(row.occurred_at)}</span>
                </div>
                {isSms ? (
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap break-words">{row.subject}</p>
                ) : (
                    <p className="text-sm text-gray-700 mt-1 break-words">{row.subject || '(no subject)'}</p>
                )}
                <div className="text-xs text-gray-400 mt-0.5">{isSms ? 'Text' : 'Email'}{row.to ? ` to ${row.to}` : ''}</div>
                <StatusChips row={row} />
            </div>
            {!isSms && (
                <button
                    type="button"
                    onClick={() => onView(row)}
                    className="text-xs font-semibold text-[#55c5d0] hover:underline flex-shrink-0 mt-0.5"
                >
                    View email
                </button>
            )}
        </div>
    );
}

export default function ContactCommunications({ contactId }) {
    const [channel, setChannel] = useState('');
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [viewing, setViewing] = useState(null);

    const load = useCallback(async (offset) => {
        const res = await getContactCommsLog(contactId, {
            channel: channel || undefined, limit: PAGE_SIZE, offset,
        });
        return res.data || {};
    }, [contactId, channel]);

    useEffect(() => {
        if (!contactId) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        load(0)
            .then((data) => {
                if (cancelled) return;
                setRows(data.results || []);
                setCounts(data.counts || null);
                setHasMore(!!data.has_more);
            })
            .catch(() => { if (!cancelled) setError('Failed to load communications.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [contactId, load]);

    const loadMore = async () => {
        setLoadingMore(true);
        try {
            const data = await load(rows.length);
            setRows((prev) => [...prev, ...(data.results || [])]);
            setHasMore(!!data.has_more);
        } catch (e) {
            setError('Failed to load more communications.');
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-4">
                {FILTERS.map((f) => (
                    <button
                        key={f.id || 'all'}
                        type="button"
                        onClick={() => setChannel(f.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                            channel === f.id
                                ? 'bg-[#55c5d0] border-[#55c5d0] text-white'
                                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        {f.label}{counts ? ` (${counts[f.countKey] ?? 0})` : ''}
                    </button>
                ))}
            </div>

            {loading && (
                <div className="flex items-center justify-center py-12 text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    Loading communications…
                </div>
            )}

            {!loading && error && (
                <div className="flex items-center gap-2 text-red-600 py-8 justify-center">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {!loading && !error && rows.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                    <Mail className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>No emails or texts recorded for this contact yet.</p>
                </div>
            )}

            {!loading && rows.length > 0 && (
                <div className="space-y-3">
                    {rows.map((row) => (
                        <CommsRow key={row.id} row={row} onView={setViewing} />
                    ))}
                    {hasMore && (
                        <button
                            type="button"
                            onClick={loadMore}
                            disabled={loadingMore}
                            className="w-full py-2.5 text-sm font-semibold text-[#55c5d0] border border-gray-100 rounded-xl hover:bg-gray-50 disabled:opacity-60"
                        >
                            {loadingMore ? 'Loading…' : 'Show older messages'}
                        </button>
                    )}
                </div>
            )}

            {viewing && (
                <MessageModal contactId={contactId} row={viewing} onClose={() => setViewing(null)} />
            )}
        </div>
    );
}
